<template>
  <div class="advantages-wrapper">
    <div class="tab-name-wrapper">
      <div
        v-for="tab in tabNames"
        :key="tab.id"
        class="tab-name"
        :class="{'tab-active': tab.id === activeTabIndex}"
        @click="changeTab(tab.id)"
      >
        {{ $t(tab.name) }}
      </div>
    </div>
    <div class="tab-wrapper">
      <template v-for="item in advantagesList" :key="item.id">
        <transition name="fade">
          <div v-show="item.id === activeTabIndex" class="tab">
            <video
              ref="myVideo"
              class="tab-video"
              no-controls
              autoplay="false"
              loop
              playsinline
              muted
            >
              <source
                v-if="typeof item.src === 'string'"
                :src="item.src"
                type="video/mp4"
              />
              <source
                v-else
                v-for="src in item.src"
                :src="src"
                type="video/mp4"
              />
            </video>
            <div class="body">
              <div class="title">
                {{ $t(item.title) }}
              </div>
              <div class="description">
                {{ $t(item.description) }}
              </div>
            </div>
          </div>
        </transition>
      </template>
    </div>
    <div class="mobile-advantages" dir="ltr">
      <div class="mobile-advantages__head-wrapper">
        <template v-for="head in advantagesList" :key="head.id">
          <div
            v-show="head.id === activeMobileIndex"
            class="mobile-advantages__head"
            :class="{'text-right': rightDir}"
          >
            <div class="mobile-advantages__head-title">
              {{ $t(head.name) }}
            </div>
            <div class="mobile-advantages__head-subtitle">
              {{ $t(head.description) }}
            </div>
          </div>
        </template>
      </div>
      <div class="mobile-advantages__main">
        <swiper
          class="mySwiper"
          :pagination="{type: 'custom'}"
          :modules="modules"
          @slide-change="handleSlide"
          @swiper="onSwiper"
        >
          <swiper-slide
            v-for="video in advantagesList"
            :key="video.id"
            class="mySwiperSlide"
          >
            <video
              class="video"
              no-controls
              autoplay
              loop
              playsinline
              muted
              style=""
            >
              <source
                v-if="typeof video.src === 'string'"
                :src="video.src"
                type="video/mp4"
              />
              <source
                v-else
                v-for="src in video.src"
                :src="src"
                type="video/mp4"
              />
            </video>
          </swiper-slide>
        </swiper>
      </div>
      <div class="dots">
        <div
          v-for="(_, dotIndex) in advantagesList"
          :key="dotIndex"
          class="dot"
          :class="{active: dotIndex === activeMobileIndex - 1}"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {useRuntimeConfig} from '#imports';
import {Swiper, SwiperSlide} from 'swiper/vue';
import {Pagination} from 'swiper';
import {tempLang} from '~/modules/CityScape/helpers/languages';
import 'swiper/scss';
import 'swiper/scss/pagination';

const rightDir = computed(() => useCookie('lang').value === 'ar');
const runtimeConfig = useRuntimeConfig();
const staticUrl = runtimeConfig.public.vueAppStaticUrl;

const myVideo = ref();
const activeTabIndex = ref(3);

const activeMobileIndex = ref(1);

const swiperAdvInstanse = ref();

function onSwiper(instanse: any) {
  swiperAdvInstanse.value = instanse;
}
function handleSlide(event: any) {
  activeMobileIndex.value = event.activeIndex + 1;
}

function changeTab(index: number) {
  activeTabIndex.value = index;
  myVideo.value.forEach((item: any, idx: number) => {
    if (idx + 1 !== activeTabIndex.value) {
      item.pause();
    } else {
      item.play();
    }
  });
}

const tabNames = ref<
  Array<{id: number; name: keyof typeof tempLang}>
>([
  {
    id: 1,
    name: 'graphics',
  },
  {
    id: 2,
    name: 'interface',
  },
  {
    id: 3,
    name: 'speed',
  },
  {
    id: 4,
    name: 'rendering',
  },
  {
    id: 5,
    name: 'showrooms',
  },
]);
const advantagesList = ref<
  Array<{
    id: number;
    name: keyof typeof tempLang;
    src: string | string[];
    title: keyof typeof tempLang;
    description: keyof typeof tempLang;
  }>
>([
  {
    id: 1,
    name: 'graphics',
    src: [`${staticUrl}/video/graphics.mp4`],
    title: 'graphicsTitle',
    description: 'graphicsTitleDesc',
  },
  {
    id: 2,
    name: 'interface',
    src: [`${staticUrl}/video/interface.mp4`],
    title: 'interfaceTitle',
    description: 'interfaceTitleDesc',
  },
  {
    id: 3,
    name: 'speed',
    src: [`${staticUrl}/video/speed.mp4`],
    title: 'speedTitle',
    description: 'speedTitleDesc',
  },
  {
    id: 4,
    name: 'rendering',
    src: [`${staticUrl}/video/renders.mp4`],
    title: 'renderingTitle',
    description: 'renderingTitleDesc',
  },
  {
    id: 5,
    name: 'showrooms',
    src: [`${staticUrl}/video/showrooms.mp4`],
    title: 'showroomsTitle',
    description: 'showroomsTitleDesc',
  },
]);

const modules = [Pagination];
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  filter: blur(2px);
}

::selection {
  background: #00deba; /* Safari */
}

::-moz-selection {
  background: #00deba; /* Firefox */
}
.rightDir {
  flex-direction: row-reverse;
  text-align: right;
}
.text-right {
  text-align: right;
}
@media (max-width: 900px) {
  .tab-name-wrapper {
    display: none !important;
  }
  .tab-wrapper {
    display: none;
  }
  .mobile-advantages {
    display: flex !important;
    width: 100%;
    height: 100%;
    flex-direction: column;
    gap: 12px;
    .dots {
      display: flex;
      gap: 4px;
      align-self: center;
      .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #b2c0cd;
        &.active {
          background: #00deba;
        }
      }
    }
    &__main {
      width: 100%;
      .mySwiper {
        width: 100%;
        .mySwiperSlide {
          width: 100%;
          .video {
            width: 100%;
            border-radius: 4px;
          }
        }
      }
    }
    &__head-wrapper {
      width: 100%;
      .mobile-advantages__head {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        &-title {
          color: #00deba;
          // text-align: right;
          font-family: Segoe UI;
          font-size: 18px;
          font-style: normal;
          font-weight: 700;
          line-height: 24px; /* 133.333% */
          letter-spacing: 0.36px;
        }
        &-subtitle {
          width: 100%;
          color: var(--white-theme-black-200, #64686e);
          font-family: Segoe UI;
          font-size: 15px;
          font-style: normal;
          font-weight: 400;
          line-height: 140%; /* 21px */
          letter-spacing: 0.45px;
        }
      }
    }
  }
}
.mobile-advantages {
  display: none;
}
.advantages-wrapper {
  width: 100%;
  height: 366px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  .tab-name-wrapper {
    height: 32px;
    width: 531px;
    display: flex;
    gap: 32px;
    align-items: center;

    .tab-name {
      color: var(--white-theme-gray-100, #b2c0cd);
      // text-align: right;
      font-family: Segoe UI;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 133.333% */
      letter-spacing: 0.36px;
      cursor: pointer;
      transition: all 0.2s ease;

      &.tab-active {
        color: #00deba;
        // text-align: right;
        font-size: 24px;
        font-weight: 700;
        line-height: normal;
        letter-spacing: 0.72px;
        cursor: default;
        transition: all 0.2s ease;
      }
    }
  }

  .tab-wrapper {
    height: 310px;
    width: 100%;
    position: relative;

    .tab {
      position: absolute;
      height: 100%;
      width: 100%;
      display: flex;
      gap: 24px;

      &-video {
        height: 100%;
        border-radius: 4px;
        width: calc((100% - 24px) / 2);
      }

      .body {
        height: 100%;
        width: calc((100% - 24px) / 2);
        display: flex;
        flex-direction: column;
        justify-content: center;

        .title {
          margin-bottom: 16px;
          color: var(--white-theme-black-100, #3d424a);
          font-family: Segoe UI;
          font-size: 24px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          letter-spacing: 0.72px;
        }

        .description {
          width: 100%;
          min-height: 66px;
          color: var(--white-theme-black-200, #64686e);
          font-family: Segoe UI;
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: 140%; /* 22.4px */
          letter-spacing: 0.48px;
        }
      }
    }
  }
}

@media screen and (max-width: 900px) {
  .advantages-wrapper {
    height: fit-content;

    .tab-name-wrapper {
      width: 100%;
      gap: 16px;
      justify-content: center;

      .tab-name {
        font-size: 14px;
      }

      .tab-active {
        font-size: 18px;
      }
    }
    .tab-wrapper {
      height: 415px;
      .tab {
        flex-direction: column;
        height: auto;

        .tab-video {
          width: 100%;
          border-radius: 4px;
        }

        .body {
          width: 100%;

          .title {
            font-size: 18px;
          }

          .description {
            font-size: 15px;
          }
        }
      }
    }
  }
}
</style>
